import React, { useState, useEffect } from "react";
import BgLatto from "../../assets/images/bg-latto.jpg";
import White from "../../assets/icons/popup/coin wallet.svg";
import Withdraw from "../../assets/icons/Withdraw.svg";
import Icon from "../../assets/icons/popup/Icon.svg";
import { withdrawalsApi } from "../../api/withdrawals";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import { useLocation } from "../../hooks/useLocation";

export default function WithdrawPopup({
  isOpen,
  onClose,
  currentBalance,
  defaultPaymentMethod,
}) {
  const { tokens } = useAuth();
  const [amount, setAmount] = useState("100");
  const [isLoading, setIsLoading] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  const [outlet, setOutlet] = useState("GCash");
  const [receiverName, setReceiverName] = useState("");
  const [bankName, setBankName] = useState("");
  const [receiverMobile, setReceiverMobile] = useState("");
  const [note, setNote] = useState("");
  const { logAction } = useLocation();

  if (!isOpen) return null;

  const handleWithdraw = async () => {
    // if (!amount || amount < 500) {
    //   return;
    // }

    setIsLoading(true);
    
    try {
      const params = {
        outlet,
        amount: amount.toString(),
        receiverName,
        receiverMobileNumber: receiverMobile,
        note,
        ...(outlet === 'Bank' && { bankName }) // Conditionally add bankName
      };

      const { status, data } = await withdrawalsApi.sendWithdrawal(
        tokens.access.token,
        params
      );

      if (status >= 200 && status < 300) {
        // Log withdrawal action with location
        logAction('withdrawal', { 
          amount: parseFloat(amount),
          outlet,
          ...(outlet === 'Bank' && { bankName })
        }).catch(error => {
          console.warn('Failed to log withdrawal location:', error);
          // Continue even if location logging fails
        });
        
        Swal.fire({
          icon: 'success',
          title: 'Withdrawal request submitted successfully!',
          showConfirmButton: false,
          timer: 1500
        });

        setAmount("100");
        setReceiverName("");
        setBankName("");
        setReceiverMobile("");
        setNote("");
        
        onClose();
        // Consider updating the balance in your global state here
      } else {
        throw new Error(data.message || 'Failed to submit withdrawal request');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      // alert(error.message || 'An error occurred during withdrawal');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 font-poppins overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4 mb-10">
        <div className="animate-slideInAndShake relative w-full max-w-sm rounded-2xl bg-[#CE1F28] overflow-hidden">
          {/* Background with gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url('${BgLatto}')`,
              backgroundBlendMode: "overlay",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.2
            }}
          ></div>

          {/* Content */}
          <div className="relative p-4">
            <button
              onClick={onClose}
              className="absolute right-2 top-2 text-white/80 hover:text-white"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Balance display */}
            <div className="mb-3 flex justify-center items-center text-white">
              <img src={White} alt="wallet icon" />
              <div className="flex flex-col">
                <h5 className="text-[10px] leading-none font-normal">
                  Current Balance
                </h5>
                <h3 className="text-4xl font-semibold">
                  {currentBalance.toLocaleString()}
                </h3>
              </div>
            </div>

            {/* Outlet selection */}
            <div className="mb-2">
              <label className="block text-white text-sm font-medium mb-1">Outlet</label>
              <select 
                value={outlet}
                onChange={(e) => setOutlet(e.target.value)}
                className="w-full bg-white rounded-lg py-3 px-4 text-gray-500"
              >
                <option value="GCash">GCash</option>
                <option value="Maya">Maya</option>
                <option value="Bank">Bank</option>
              </select>
            </div>

            {/* Amount input */}
            <div className="mb-2 relative">
              <div className="text-white text-sm font-medium mb-1">Amount</div>
              {/* Input Field */}
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-white rounded-lg py-3 px-4 text-lg font-normal text-gray-500"
                placeholder="Enter amount"
              />
            </div>

            {/* Receiver Name */}
            <div className="mb-2">
              <label className="block text-white text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                className="w-full bg-white rounded-lg py-3 px-4 text-gray-500"
              />
            </div>

            {/* Conditional Bank Name */}
            {outlet === 'Bank' && (
              <div className="mb-2">
                <label className="block text-white text-sm font-medium mb-1">Bank Name</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full bg-white rounded-lg py-3 px-4 text-gray-500"
                />
              </div>
            )}

            {/* Receiver Mobile/Bank Account */}
            <div className="mb-2">
              <label className="block text-white text-sm font-medium mb-1">
                Mobile Number or Bank Account no.
              </label>
              <input
                type="text"
                value={receiverMobile}
                onChange={(e) => setReceiverMobile(e.target.value)}
                className="w-full bg-white rounded-lg py-3 px-4 text-gray-500"
              />
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-white text-sm font-medium mb-1">Notes</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-white rounded-lg py-3 px-4 text-gray-500 h-20"
              />
            </div>

            {/* Button group */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-white text-[#CE1F28] py-3 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                className="bg-[#CE1F28] text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                onClick={handleWithdraw}
                disabled={isLoading}
              >
                {isLoading ? (
                  'Processing...'
                ) : (
                  <>
                    <img src={Withdraw} alt="withdraw icon" />
                    WITHDRAW
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
